import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI, storesAPI } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { BottomNav } from './MarketplaceHome';
import {
  ArrowLeft,
  ShoppingCart,
  Heart,
  Share2,
  Minus,
  Plus,
  Store,
  MapPin,
  Phone,
  ChevronLeft,
  ChevronRight,
  Play,
  Check,
  ZoomIn,
  X,
  Info,
  Package,
  Facebook,
  MessageCircle,
  Link as LinkIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';
import { ReviewsSection } from '../components/ReviewsSection';
import { toast } from 'sonner';

export const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, itemCount } = useCart();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState({}); // { [group_id]: option_id }
  const [zoomOpen, setZoomOpen] = useState(false);
  const [related, setRelated] = useState({ same_store: [], similar: [] });
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    loadProduct();
    productsAPI.getRelated(id, 8).then(({ data }) => setRelated(data || { same_store: [], similar: [] })).catch(() => {});
  }, [id]);

  const loadProduct = async () => {
    try {
      const { data: productData } = await productsAPI.get(id);
      setProduct(productData);
      
      if (productData.store_id) {
        const { data: storeData } = await storesAPI.get(productData.store_id);
        setStore(storeData);
      }
    } catch (error) {
      toast.error('Produto não encontrado');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const handleAddToCart = () => {
    if (hasVariations && !allVariationsChosen) {
      toast.error('Selecione todas as opções antes de continuar.');
      return;
    }
    const selections = variationSelectionsPayload();
    const enhancedProduct = {
      ...product,
      // Effective price after variations
      price: finalPrice,
      // Store selections so cart/order can display them
      selected_variations: selections,
      variation_key: selections.map((s) => `${s.group_id}:${s.option_id}`).join('|'),
    };
    for (let i = 0; i < quantity; i++) {
      addItem(enhancedProduct);
    }
    setAddedToCart(true);
    toast.success(`${quantity}x ${product.name} adicionado ao carrinho!`);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/carrinho');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length > 0 ? product.images : ['https://via.placeholder.com/400x400?text=Sem+Imagem'];
  const discount = product.promo_price ? Math.round((1 - product.promo_price / product.price) * 100) : 0;

  // Variations: compute selected option per group and price adjustment
  const variations = product.variations || [];
  const hasVariations = variations.length > 0;
  const selectedOptions = variations.map((g) => {
    const opt = g.options?.find((o) => o.id === selectedVariations[g.id]);
    return { group: g, option: opt };
  });
  const allVariationsChosen = !hasVariations || selectedOptions.every((s) => !!s.option);
  const basePrice = product.promo_price ?? product.price;
  const priceDelta = selectedOptions.reduce((sum, s) => sum + (s.option?.price_delta || 0), 0);
  const finalPrice = basePrice + priceDelta;
  // Override main image if a selected option has its own image
  const variationImage = selectedOptions.slice().reverse().find((s) => s.option?.image_url)?.option?.image_url;
  const displayImages = variationImage ? [variationImage, ...images.filter((i) => i !== variationImage)] : images;
  const activeImage = displayImages[currentImageIndex] || displayImages[0];

  const selectVariation = (groupId, optionId) => {
    setSelectedVariations((prev) => ({ ...prev, [groupId]: optionId }));
    setCurrentImageIndex(0);
  };

  const variationSelectionsPayload = () =>
    selectedOptions
      .filter((s) => s.option)
      .map((s) => ({
        group_id: s.group.id,
        group_name: s.group.name,
        option_id: s.option.id,
        option_label: s.option.label,
        price_delta: s.option.price_delta || 0,
      }));

  const nextImage = () => {
    if (displayImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
    }
  };

  const prevImage = () => {
    if (displayImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="product-details-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="font-semibold text-gray-900 truncate flex-1 mx-4">{product.name}</h1>
          <button onClick={() => navigate('/carrinho')} className="p-2 -mr-2 relative" data-testid="cart-btn">
            <ShoppingCart className="w-6 h-6 text-gray-700" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Image Gallery */}
      <div className="relative bg-gray-100">
        <div className="aspect-square relative overflow-hidden">
          <img
            src={activeImage}
            alt={product.name}
            data-testid="product-main-image"
            className="w-full h-full object-cover"
          />
          
          {product.promo_price && (
            <span className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
              -{discount}%
            </span>
          )}
          
          {displayImages.length > 1 && (
            <>
              <button
                onClick={prevImage}
                data-testid="gallery-prev-btn"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center shadow-lg"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                data-testid="gallery-next-btn"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center shadow-lg"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>

        {/* Image indicators */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                data-testid={`gallery-indicator-${index}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex ? 'bg-blue-600 w-6' : 'bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {displayImages.length > 1 && (
        <div className="flex gap-2 p-4 overflow-x-auto hide-scrollbar">
          {displayImages.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              data-testid={`gallery-thumb-${index}`}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                index === currentImageIndex ? 'border-blue-600' : 'border-transparent'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Product Info */}
      <div className="px-4 py-4">
        <h2 className="text-xl font-bold text-gray-900">{product.name}</h2>
        
        {/* Price */}
        <div className="mt-3">
          {product.promo_price ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-gray-400 line-through text-lg">{formatPrice(product.price)}</span>
              <span className="text-3xl font-bold text-green-600" data-testid="product-final-price">{formatPrice(finalPrice)}</span>
            </div>
          ) : (
            <span className="text-3xl font-bold text-gray-900" data-testid="product-final-price">{formatPrice(finalPrice)}</span>
          )}
          {priceDelta !== 0 && (
            <p className="text-xs text-blue-600 mt-1" data-testid="variation-price-delta">
              {priceDelta > 0 ? '+' : '-'}{formatPrice(Math.abs(priceDelta))} pelas opções selecionadas
            </p>
          )}
          <p className="text-sm text-gray-500 mt-1">Em até 12x sem juros</p>
        </div>

        {/* Variations selector — Admin publishes; customer chooses */}
        {hasVariations && (
          <div className="mt-6 space-y-4" data-testid="variations-selector">
            {variations.map((group) => {
              const selectedId = selectedVariations[group.id];
              return (
                <div key={group.id} data-testid={`variation-group-${group.id}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">
                      {group.name}
                      <span className="text-red-500 ml-1">*</span>
                    </h3>
                    {selectedId && (
                      <span className="text-xs text-slate-500">
                        {group.options.find((o) => o.id === selectedId)?.label}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.options.map((opt) => {
                      const active = selectedId === opt.id;
                      const delta = Number(opt.price_delta) || 0;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => selectVariation(group.id, opt.id)}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                            active
                              ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm ring-2 ring-blue-100'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
                          }`}
                          data-testid={`variation-option-${group.id}-${opt.id}`}
                        >
                          {opt.image_url && (
                            <img src={opt.image_url} alt="" className="w-6 h-6 rounded object-cover" />
                          )}
                          <span>{opt.label}</span>
                          {delta !== 0 && (
                            <span className={`text-[11px] ${delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {delta > 0 ? '+' : '-'}{formatPrice(Math.abs(delta))}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {!allVariationsChosen && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="variation-warning">
                Selecione uma opção em cada grupo para continuar.
              </p>
            )}
          </div>
        )}

        {/* Description */}
        {product.description && (
          <div className="mt-6">
            <h3 className="font-semibold text-gray-900 mb-2">Descrição</h3>
            <p className="text-gray-600 leading-relaxed">{product.description}</p>
          </div>
        )}
      </div>

      {/* Store Info */}
      {store && (
        <div className="mx-4 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="w-full h-full rounded-xl object-cover" />
              ) : (
                <Store className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 truncate">{store.name}</h4>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {store.city}/{store.state}
              </p>
            </div>
            <button
              onClick={() => navigate(`/loja/${store.id}`)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg"
            >
              Ver Loja
            </button>
          </div>
        </div>
      )}

      {/* Quantity Selector */}
      <div className="px-4 py-6">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-900">Quantidade</span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center"
              data-testid="qty-minus"
            >
              <Minus className="w-5 h-5 text-gray-600" />
            </button>
            <span className="text-xl font-bold w-8 text-center" data-testid="qty-value">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-10 h-10 rounded-full border-2 border-blue-600 bg-blue-600 flex items-center justify-center"
              data-testid="qty-plus"
            >
              <Plus className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Video */}
      {product.video_url && (
        <div className="px-4 pb-4" data-testid="product-video">
          <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 mb-2">Vídeo do produto</h3>
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            <video src={product.video_url} controls playsInline className="w-full h-full object-cover">
              <track kind="captions" />
            </video>
          </div>
        </div>
      )}

      {/* Especificações técnicas */}
      {product.specs && product.specs.length > 0 && (
        <div className="px-4 pb-4" data-testid="product-specs">
          <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 mb-2 flex items-center gap-2">
            <Package className="w-4 h-4" /> Características técnicas
          </h3>
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
            {product.specs.map((sp, i) => (
              <div key={i} className="flex px-4 py-2.5 text-sm" data-testid={`spec-${i}`}>
                <span className="w-1/2 text-slate-500">{sp.key}</span>
                <span className="w-1/2 text-slate-900 font-medium">{sp.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Informações importantes */}
      {product.additional_info && (
        <div className="px-4 pb-4" data-testid="product-additional-info">
          <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" /> Informações importantes
          </h3>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 whitespace-pre-line">
            {product.additional_info}
          </div>
        </div>
      )}

      {/* Compartilhar */}
      <div className="px-4 pb-4" data-testid="product-share">
        <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 mb-2">Compartilhar</h3>
        <div className="flex flex-wrap gap-2">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${product.name} - ${window.location.href}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
            data-testid="share-whatsapp"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium"
            data-testid="share-facebook"
          >
            <Facebook className="w-4 h-4" /> Facebook
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copiado!'); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm font-medium"
            data-testid="share-copy-link"
          >
            <LinkIcon className="w-4 h-4" /> Copiar link
          </button>
        </div>
      </div>

      {/* Produtos da mesma loja */}
      {related.same_store && related.same_store.length > 0 && (
        <div className="px-4 pb-4" data-testid="related-same-store">
          <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 mb-3">Da mesma loja</h3>
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory hide-scrollbar">
            {related.same_store.map((rp) => (
              <Link key={rp.id} to={`/produto/${rp.id}`} className="flex-shrink-0 w-40 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`related-store-${rp.id}`}>
                <div className="aspect-square bg-slate-50">
                  <img src={optimizeCloudinaryUrl(rp.images?.[0], { width: 400 })} alt={rp.name} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium text-slate-800 line-clamp-2 min-h-[2rem]">{rp.name}</p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{formatPrice(rp.promo_price || rp.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Produtos similares */}
      {related.similar && related.similar.length > 0 && (
        <div className="px-4 pb-4" data-testid="related-similar">
          <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 mb-3">Produtos semelhantes</h3>
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory hide-scrollbar">
            {related.similar.map((rp) => (
              <Link key={rp.id} to={`/produto/${rp.id}`} className="flex-shrink-0 w-40 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`related-similar-${rp.id}`}>
                <div className="aspect-square bg-slate-50">
                  <img src={optimizeCloudinaryUrl(rp.images?.[0], { width: 400 })} alt={rp.name} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium text-slate-800 line-clamp-2 min-h-[2rem]">{rp.name}</p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{formatPrice(rp.promo_price || rp.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Etapa 3 — Avaliações do Produto */}
      <ReviewsSection targetType="product" targetId={product.id} />

      {/* Zoom modal (lightbox) */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoomOpen(false)}
          data-testid="image-zoom-modal"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center"
            data-testid="close-zoom-btn"
          >
            <X className="w-5 h-5 text-slate-900" />
          </button>
          <img src={activeImage} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t p-4 flex gap-3">
        <button
          onClick={handleAddToCart}
          disabled={addedToCart || (hasVariations && !allVariationsChosen)}
          className={`flex-1 py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all ${
            addedToCart
              ? 'bg-green-500 text-white'
              : hasVariations && !allVariationsChosen
              ? 'bg-slate-200 text-slate-600 cursor-not-allowed border border-slate-300'
              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }`}
          data-testid="add-to-cart-btn"
        >
          {addedToCart ? (
            <>
              <Check className="w-5 h-5" />
              Adicionado!
            </>
          ) : (
            <>
              <ShoppingCart className="w-5 h-5" />
              Adicionar
            </>
          )}
        </button>
        <button
          onClick={handleBuyNow}
          disabled={hasVariations && !allVariationsChosen}
          className={`flex-1 py-4 rounded-xl font-semibold text-lg transition-colors ${
            hasVariations && !allVariationsChosen
              ? 'bg-slate-300 text-slate-700 cursor-not-allowed border border-slate-400'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          data-testid="buy-now-btn"
        >
          Comprar Agora
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default ProductDetails;
